import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiPermits } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { PERMIT_TYPES, statusLabel } from '@/constants/permits';
import { DASHBOARD } from '@/constants/testIds';
import { FileText, FilePlus, ArrowRight, Calendar, AlertCircle, CheckCircle2, FileEdit, Clock } from 'lucide-react';

function StatCard({ label, value, accent, testid, icon: Icon }) {
  return (
    <div data-testid={testid} className="section-card relative overflow-hidden">
      <div className="flex items-start justify-between">
        <div>
          <div className="label-industrial">{label}</div>
          <div className="text-5xl font-display font-black tracking-tighter text-[#09090B] mt-2">
            {value}
          </div>
        </div>
        {Icon && <Icon className="w-6 h-6 text-stone-300" strokeWidth={2} />}
      </div>
      <div className={`absolute bottom-0 left-0 h-1 ${accent}`} style={{ width: '100%' }}></div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, brouillons: 0, soumis: 0, approuves: 0, rejetes: 0, cloture: 0 });
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([
          apiPermits.stats(),
          apiPermits.list({ }),
        ]);
        setStats(s.data);
        setRecent(r.data.slice(0, 6));
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, []);

  const typeLabel = (id) => PERMIT_TYPES.find((t) => t.id === id)?.label || id;

  return (
    <div className="max-w-7xl space-y-8">
      {/* Hero */}
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-stone-500 mb-2">
          Tableau de bord
        </div>
        <h1 className="text-4xl md:text-5xl font-display font-black tracking-tighter text-[#09090B]">
          Bonjour, {user?.name?.split(' ')[0]}.
        </h1>
        <p className="text-stone-600 mt-3 max-w-2xl">
          Vue d'ensemble de l'activité des autorisations de travail sur vos sites attribués.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total permis" value={stats.total} accent="bg-[#09090B]" testid={DASHBOARD.statTotal} icon={FileText} />
        <StatCard label="Brouillons" value={stats.brouillons} accent="bg-stone-400" testid={DASHBOARD.statDrafts} icon={FileEdit} />
        <StatCard label="Soumis" value={stats.soumis} accent="bg-blue-600" testid={DASHBOARD.statSubmitted} icon={Clock} />
        <StatCard label="Approuvés" value={stats.approuves} accent="bg-green-600" testid={DASHBOARD.statApproved} icon={CheckCircle2} />
      </div>

      {/* Quick actions */}
      <div className="grid md:grid-cols-3 gap-4">
        {PERMIT_TYPES.map((t) => (
          <button
            key={t.id}
            onClick={() => navigate(`/permits/new?type=${t.id}`)}
            data-testid={`quick-new-${t.id}`}
            className="section-card text-left hover:border-[#09090B] hover:border-2 transition-all group"
          >
            <div className={`inline-flex items-center text-[11px] font-bold uppercase tracking-wider px-2 py-0.5 ${t.color} border`}>
              {t.short}
            </div>
            <div className="text-xl font-display font-bold mt-4 text-[#09090B]">{t.label}</div>
            <div className="text-sm text-stone-600 mt-2 leading-relaxed">{t.description}</div>
            <div className="flex items-center gap-2 mt-6 text-[12px] font-bold uppercase tracking-wider text-[#DA291C] group-hover:gap-3 transition-all">
              <FilePlus className="w-4 h-4" />
              <span>Créer un permis</span>
              <ArrowRight className="w-4 h-4" />
            </div>
          </button>
        ))}
      </div>

      {/* Recent permits */}
      <div className="section-card">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="label-industrial">Activité récente</div>
            <h2 className="text-2xl font-display font-bold tracking-tight">Derniers permis</h2>
          </div>
          <button
            onClick={() => navigate('/permits')}
            data-testid={DASHBOARD.newPermitCta}
            className="btn-industrial-secondary"
          >
            Voir tout
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-stone-500">Chargement...</div>
        ) : recent.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-stone-200">
            <AlertCircle className="w-10 h-10 text-stone-300 mx-auto mb-3" />
            <div className="text-sm font-bold uppercase tracking-wider text-stone-500 mb-2">
              Aucun permis pour le moment
            </div>
            <button
              onClick={() => navigate('/permits/new')}
              className="btn-industrial-primary mt-4"
            >
              Créer le premier permis
            </button>
          </div>
        ) : (
          <div data-testid={DASHBOARD.recentList} className="divide-y divide-stone-200">
            {recent.map((p) => (
              <button
                key={p.id}
                onClick={() => navigate(`/permits/${p.id}`)}
                className="w-full py-4 flex items-center gap-4 hover:bg-stone-50 -mx-6 px-6 transition-colors text-left"
              >
                <div className="font-mono text-xs text-stone-500 w-32 truncate">{p.numero}</div>
                <div className="flex-1">
                  <div className="font-semibold text-[#09090B] text-sm">{p.titre}</div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {typeLabel(p.type)} • {p.site_code} • {p.created_by_name}
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs text-stone-500">
                  <Calendar className="w-3.5 h-3.5" />
                  {p.date_debut}
                </div>
                <span className={`badge-status badge-${p.status}`}>{statusLabel(p.status)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
